import React, { useEffect, useState, useMemo } from "react";
import { 
    Box, Container, Grid, Card, Typography, Stack, Avatar 
} from "@mui/material";

// Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faShoppingCart, faClockRotateLeft, faCheckCircle, 
    faTimesCircle, faReceipt 
} from "@fortawesome/free-solid-svg-icons";

// Componentes QUD & Controllers
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import QUDProgress from "../../../components/progress";
import OrdersDataTable from "./OrdersdataTable";
import { getOrders } from "../../../controllers/company/OrdersController";

export default function Orders() {
    const [isLoading, setIsLoading] = useState(false);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        window.scrollTo(0, 0);
        handleGetOrders();
    }, []);

    const handleGetOrders = async () => {
        setIsLoading(true);
        let response = await getOrders();
        if (response.success === true) {
            setOrders(response.data.orders || []);
        }
        setIsLoading(false);
    }

    // --- CÁLCULO DE KPIs (Estadísticas de Pedidos) ---
    const stats = useMemo(() => {
        const total = orders.length;
        
        // NOTA: Ajusta estas palabras clave ('pending', 'completed', etc.) 
        // según cómo venga el campo "status" o "estatus" desde tu backend Laravel.
        const pendientes = orders.filter(o => 
            o.status === 'pending' || o.status === 'por_procesar' || o.status === 'pagado'
        ).length;
        
        const completados = orders.filter(o => 
            o.status === 'completed' || o.status === 'entregado'
        ).length;
        
        const cancelados = orders.filter(o => 
            o.status === 'cancelled' || o.status === 'rechazado' || o.status === 'cancelado'
        ).length;

        return { total, pendientes, completados, cancelados };
    }, [orders]);

    // Estilo base unificado para las tarjetas
    const cardStyle = {
        elevation: 0,
        sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: 2.5, bgcolor: '#ffffff' }
    };

    return (
        <Container maxWidth='xl' sx={{ py: 4 }}>
            <QUDProgress open={isLoading && orders.length === 0} />

            <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Pedidos', link: '/orders' }]} />
            
            {/* --- HEADER PRINCIPAL --- */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#222' }}>
                        Panel de Pedidos
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Administra las compras, envíos y el historial de transacciones de tus clientes.
                    </Typography>
                </Box>
            </Box>

            {/* --- CABECERA DE ESTADÍSTICAS (KPIs) --- */}
            {(orders.length > 0 || isLoading) && (
                <Grid container spacing={3} mb={4}>
                    
                    {/* KPI 1: Total Pedidos */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faShoppingCart} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Total Pedidos
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.total}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 2: Pendientes (Alerta / Atención requerida) */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faClockRotateLeft} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Por Procesar
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.pendientes}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 3: Completados */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faCheckCircle} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Completados
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.completados}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 4: Cancelados */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#ffebee', color: '#c62828', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faTimesCircle} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Cancelados
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.cancelados}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                </Grid>
            )}

            {/* --- CONTENIDO PRINCIPAL --- */}
            <Box>
                {orders.length !== 0 ? (
                    <OrdersDataTable orders={orders} />
                ) : (
                    !isLoading && (
                        /* EMPTY STATE */
                        <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 3, boxShadow: 'none', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box sx={{ width: 80, height: 80, bgcolor: '#f8f9fa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                <FontAwesomeIcon icon={faReceipt} size="3x" color="#ccc" />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#333' }} gutterBottom>
                                Aún no tienes pedidos registrados
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', maxWidth: 450 }}>
                                Cuando tus clientes realicen compras, reservas o pagos desde tu catálogo digital, los detalles aparecerán aquí para que los proceses.
                            </Typography>
                        </Card>
                    )
                )}
            </Box>
            
        </Container>
    );
}